import {
  ArrowLeft,
  ArrowRight,
  Edit3,
  Loader2,
  Mail,
  MapPin,
  Phone,
  ShieldCheck,
  ShoppingBag,
  Store,
  Users,
  UtensilsCrossed,
} from "lucide-react";
import {
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Link,
  useParams,
} from "react-router-dom";

import establishmentsApi from "../../api/establishmentsApi";
import { useLanguage } from "../../contexts/LanguageContext";
import translateApiError from "../../utils/translateApiError";
import "../../styles/Establishments/establishment-details.css";

const translateFallback = (
  t,
  key,
  fallback,
) => {
  const value = t(key);

  return value === key
    ? fallback
    : value;
};

const getRecord = (response) => {
  return (
    response?.data?.data ||
    response?.data ||
    null
  );
};

const formatDate = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (
    Number.isNaN(
      date.getTime(),
    )
  ) {
    return value;
  }

  const day = String(
    date.getDate(),
  ).padStart(2, "0");

  const month = String(
    date.getMonth() + 1,
  ).padStart(2, "0");

  const year =
    date.getFullYear();

  return `${day}/${month}/${year}`;
};

const metricValue = (
  record,
  keys,
) => {
  for (const key of keys) {
    const value =
      record?.[key];

    if (
      value !== undefined &&
      value !== null
    ) {
      return value;
    }
  }

  return 0;
};

function EstablishmentDetails() {
  const {
    establishmentId,
  } = useParams();

  const { t } =
    useLanguage();

  const [record, setRecord] =
    useState(null);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  const [
    direction,
    setDirection,
  ] = useState(
    document.documentElement.dir ||
      "ltr",
  );

  const isRtl =
    direction === "rtl";

  // Keep this page synchronized with the global language direction.
  useEffect(() => {
    const updateDirection = () => {
      setDirection(
        document.documentElement.dir ||
          "ltr",
      );
    };

    updateDirection();

    const observer =
      new MutationObserver(
        updateDirection,
      );

    observer.observe(
      document.documentElement,
      {
        attributes: true,
        attributeFilter: [
          "dir",
          "lang",
        ],
      },
    );

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const loadRecord =
      async () => {
        try {
          setLoading(true);
          setError("");

          const response =
            await establishmentsApi.getEstablishment(
              establishmentId,
            );

          setRecord(
            getRecord(
              response,
            ),
          );
        } catch (
          requestError
        ) {
          setError(
            translateApiError(
              requestError,
              t,
            ),
          );
        } finally {
          setLoading(false);
        }
      };

    loadRecord();
  }, [
    establishmentId,
    t,
  ]);

  const stats =
    useMemo(() => {
      return [
        {
          key: "menus",
          label:
            translateFallback(
              t,
              "establishments.details.stats.menus",
              "Menus",
            ),
          value:
            metricValue(
              record,
              [
                "menus_count",
                "active_menus_count",
              ],
            ),
          icon: UtensilsCrossed,
        },
        {
          key: "products",
          label:
            translateFallback(
              t,
              "establishments.details.stats.products",
              "Products",
            ),
          value:
            metricValue(
              record,
              [
                "products_count",
                "active_products_count",
              ],
            ),
          icon: Store,
        },
        {
          key: "orders",
          label:
            translateFallback(
              t,
              "establishments.details.stats.orders",
              "Orders",
            ),
          value:
            metricValue(
              record,
              [
                "orders_count",
                "today_orders_count",
              ],
            ),
          icon: ShoppingBag,
        },
        {
          key: "staff",
          label:
            translateFallback(
              t,
              "establishments.details.stats.staff",
              "Staff",
            ),
          value:
            metricValue(
              record,
              [
                "staff_count",
                "active_staff_count",
              ],
            ),
          icon: Users,
        },
      ];
    }, [
      record,
      t,
    ]);

  if (loading) {
    return (
      <main
        className={`establishment-details-page ${
          isRtl
            ? "establishment-details-page--rtl"
            : "establishment-details-page--ltr"
        }`}
        dir={direction}
      >
        <section className="establishment-details-page__state">
          <Loader2
            size={30}
            className="establishment-details-page__spin"
          />

          <strong>
            {translateFallback(
              t,
              "establishments.details.loading",
              "Loading establishment...",
            )}
          </strong>
        </section>
      </main>
    );
  }

  if (
    error ||
    !record
  ) {
    return (
      <main
        className={`establishment-details-page ${
          isRtl
            ? "establishment-details-page--rtl"
            : "establishment-details-page--ltr"
        }`}
        dir={direction}
      >
        <section className="establishment-details-page__state">
          <strong>
            {translateFallback(
              t,
              "establishments.details.notFound",
              "Establishment not found.",
            )}
          </strong>

          {error ? (
            <span>
              {error}
            </span>
          ) : null}

          <Link to="/establishments">
            {translateFallback(
              t,
              "establishments.form.backToEstablishments",
              "Back to establishments",
            )}
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main
      className={`establishment-details-page ${
        isRtl
          ? "establishment-details-page--rtl"
          : "establishment-details-page--ltr"
      }`}
      dir={direction}
    >
      <section className="establishment-details-page__hero">
        <Link
          to="/establishments"
          className="establishment-details-page__back"
        >
          {isRtl ? (
            <ArrowRight
              size={18}
            />
          ) : (
            <ArrowLeft
              size={18}
            />
          )}

          <span>
            {translateFallback(
              t,
              "establishments.form.backToEstablishments",
              "Back to establishments",
            )}
          </span>
        </Link>

        <div className="establishment-details-page__hero-main">
          <div className="establishment-details-page__hero-copy">
            <p className="establishment-details-page__eyebrow">
              <ShieldCheck
                size={15}
              />

              {translateFallback(
                t,
                "establishments.details.eyebrow",
                "Establishment details",
              )}
            </p>

            <h1>
              {record.name}
            </h1>

            <p>
              {record.description ||
                translateFallback(
                  t,
                  "establishments.details.noDescription",
                  "No description available.",
                )}
            </p>
          </div>

          <div className="establishment-details-page__hero-actions">
            <Link
              to={`/establishments/${record.id}/edit`}
              className="establishment-details-page__primary-button"
            >
              <Edit3
                size={17}
              />

              <span>
                {translateFallback(
                  t,
                  "establishments.actions.edit",
                  "Edit",
                )}
              </span>
            </Link>
          </div>
        </div>
      </section>

      <section className="establishment-details-page__stats">
        {stats.map(
          (stat) => {
            const Icon =
              stat.icon;

            return (
              <article
                key={
                  stat.key
                }
              >
                <span className="establishment-details-page__stat-icon">
                  <Icon
                    size={20}
                  />
                </span>

                <div>
                  <span>
                    {
                      stat.label
                    }
                  </span>

                  <strong>
                    {
                      stat.value
                    }
                  </strong>
                </div>
              </article>
            );
          },
        )}
      </section>

      <div className="establishment-details-page__grid">
        <section className="establishment-details-page__panel">
          <div className="establishment-details-page__panel-header">
            <h2>
              {translateFallback(
                t,
                "establishments.details.information",
                "Information",
              )}
            </h2>
          </div>

          <dl className="establishment-details-page__definition-list">
            <div>
              <dt>
                {translateFallback(
                  t,
                  "establishments.form.businessId",
                  "Business ID",
                )}
              </dt>

              <dd dir="ltr">
                {record.business_id ||
                  record
                    .business
                    ?.id ||
                  "-"}
              </dd>
            </div>

            <div>
              <dt>
                {translateFallback(
                  t,
                  "establishments.table.business",
                  "Business",
                )}
              </dt>

              <dd>
                {record.business
                  ?.name ||
                  record.business_name ||
                  "-"}
              </dd>
            </div>

            <div>
              <dt>
                {translateFallback(
                  t,
                  "establishments.table.manager",
                  "Manager",
                )}
              </dt>

              <dd>
                {record.owner
                  ?.name ||
                  record.manager
                    ?.name ||
                  "-"}
              </dd>
            </div>

            <div>
              <dt>
                {translateFallback(
                  t,
                  "establishments.form.type",
                  "Type",
                )}
              </dt>

              <dd>
                {translateFallback(
                  t,
                  `establishments.types.${record.type}`,
                  record.type ||
                    "-",
                )}
              </dd>
            </div>

            <div>
              <dt>
                {translateFallback(
                  t,
                  "establishments.form.defaultLanguage",
                  "Default language",
                )}
              </dt>

              <dd>
                {translateFallback(
                  t,
                  `languages.${
                    record.default_language ||
                    record.default_locale
                  }`,
                  record.default_language ||
                    record.default_locale ||
                    "-",
                )}
              </dd>
            </div>

            <div>
              <dt>
                {translateFallback(
                  t,
                  "establishments.table.created",
                  "Created",
                )}
              </dt>

              <dd dir="ltr">
                {formatDate(
                  record.created_at,
                )}
              </dd>
            </div>
          </dl>
        </section>

        <section className="establishment-details-page__panel">
          <div className="establishment-details-page__panel-header">
            <h2>
              {translateFallback(
                t,
                "establishments.form.contact",
                "Contact",
              )}
            </h2>
          </div>

          <div className="establishment-details-page__contact-list">
            <span>
              <Phone
                size={17}
              />

              <strong dir="ltr">
                {record.phone ||
                  "-"}
              </strong>
            </span>

            <span>
              <Phone
                size={17}
              />

              <strong dir="ltr">
                {record.whatsapp ||
                  "-"}
              </strong>
            </span>

            <span>
              <Mail
                size={17}
              />

              <strong dir="ltr">
                {record.email ||
                  "-"}
              </strong>
            </span>

            <span>
              <MapPin
                size={17}
              />

              <strong>
                {record.address ||
                  record.adresse ||
                  "-"}
              </strong>
            </span>
          </div>
        </section>
      </div>
    </main>
  );
}

export default EstablishmentDetails;